/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Data, Link} from '../../shared/api';
import {PayStatus} from '../../shared/employees/employees.model';
import {FileTags} from '../../shared/files/files.model';
import {PayExecAttributes} from '../analyze/pay-results.model';

/**
 * Information about a specific mass action button
 */
export interface ButtonDetail {
  count: number;
  type: string;
}

/**
 * Information about mass action buttons (launch, freeze, done).
 */
export interface ButtonInfos {
  total: number;
  detail: Array<ButtonDetail>;
}

/**
 * Pay response as returned by the server
 */
export interface PayResponse {
  datas: Array<Pay>;
  buttonAllInfos: ButtonInfos;
}

export interface OngoingPayrollsResponse {
  total: number;
  payload: PayResponse;
}

/**
 * Pay execution data : detail about an execution of a pay
 */
export interface PayExecutionData {
  id: number;
  links: Array<Link>;
  time: string;
  payslipPdfFileId: number;
  payslipHtmlFileId: number;
  requestPerYear?: any;
}

export interface PayExecBody {
  id: number;
  attributes: Data<PayExecAttributes>;
}

/**
 * A Pay as returned by the pay web service
 */
export interface Pay {
  id: number;
  externalID: string;
  statistic?: any;
  firstName: string;
  lastName: string;
  payRollSerieName?: string;
  payRollPeriodsName?: string;
  nbLaunch: number;
  status: PayStatus;
  nbCriticalAlert: number;
  nbTotalAlert: number;
  nbExecution: number;
  lastExecution: PayExecutionData;
  lastGoodExecution: PayExecutionData;
  contractID: number;
  actions: Array<string>;
  readOnly?: boolean;
  payPeriodId?: number;
  isOutsource?: boolean;
  payTypeName?: string;
  payTypeTransId?: string;
  payTypeCalculationType?: string;
  payTypeAllowLaunchAfterClose: boolean;
  payTypeOnlyAdmin: boolean;
  payTypeRequiredPayTypeNames: Array<string>;
  externalNumber: string;
  retroActivityCalculationCount?: number;
  // computeDate
  // updateDate
  payslipStatus: PayslipPaymentStatus;
  paymentStatus: PayslipPaymentStatus;
  payslipModality: string;
  isUnalterable: boolean;
  cliRegId?: number;
  cliRegNickname?: string;
  contractBeginDate?: string;
  contractEndDate?: string;
  serieId: number;
  links: Array<Link>;

  execution?: PayExecutionData;
  name: string;
  payRollName: string;
  attributes?: { shortId: number };
  payslipPdfFileId: number;
  payslipHtmlFileId: number;
  payslipFileTags?: FileTags;
  selected?: boolean;
}

/**
 * Possible pay actions
 */
export enum Action {
  LAUNCH,
  FIX,
  DONE,
  LAST_DONE,
  READY,
  UNFREEZE,
  CREATE_NEXT_PPC,
  VALIDATE,
  ALARM,
  NONE
}

export enum PayslipPaymentStatus {
  TODO = 'TODO',
  SENT = 'SENT',
  IN_PROGRESS = 'IN_PROGRESS',
  ERROR = 'ERROR'
}

// Statistics interfaces

/**
 * Detail of a pay status
 */
export interface PayStatusDetail {
  count: number;
  type: string;
}

/**
 * Payroll status
 */
export interface PayrollStatus {
  total: number;
  detail: Array<PayStatusDetail>;
}

/**
 * Represents a pay request
 */
export interface PayRequest {
  id?: number;
  attributes?: PayRequestAttributes;
}

export interface PayRequestAttributes {
  paystatus: string;
  launchDate: Date;
  progress: number;
  shortId?: number;
  requestPerYear?: any;
}

/**
 * Represent a pay serie
 */
export interface PaySerie {
  id: number;
  name: string;
  progress: number;
  closingdate: string;
}

/**
 * Global statistics wrapper
 */
export interface OngoingPayrollStatistics {
  payrollStatus: PayrollStatus;
  latestRequests: Array<PayRequest>;
  ongoingPayrolls: number;
  finishedPayrolls: number;
  ongoingCalculations: number;
  contractsManaged: number;
  paySeries: Array<PaySerie>;

  latestFinishedRequest?: PayRequest;
}

export interface PayrollStatusData {
  payrollStatus: PayrollStatus;
}

export interface RetroContractChangesData {
  begin: Date;
  end: Date;
  retro: Date;
  absenceRetro: Date;
  variableRetro: Date;
  retroByGroupName: any;
  groupZoneRetroList: Array<ContractRetroItem>;
  absenceRetroList: Array<ContractRetroItem>;
  variableRetroList: Array<ContractRetroItem>;
}

export interface ContractRetroItem {
  item: string;
  itemId: number;
  itemParentId: number;
  groupZoneName: string;
  groupZoneTransId: string;
  retroDate: Date;
}
